import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useDecideMtoRunMutation,
  useDeleteMtoRunMutation,
  useGetMtoRunQuery,
  useLazyExportMtoMemoQuery,
  useLazyExportMtoRunQuery,
  useRetryMtoRunMutation,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import {
  apiErrorMessage,
  approvalBlockingCount,
  exportFileName,
  fmtDateTime,
  isMtoModuleId,
  isMtoRunActive,
  runRaw,
  saveBlob,
} from '@entities/mto';
import { useAgentPermission } from '@entities/user';
import { ComparisonSection } from '../MtoComparison';
import { MtoVedomostView } from '../MtoVedomostView';
import { MtoReconcileView } from '../MtoReconcileView';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { ConfirmDialog } from '@shared/ui/ConfirmDialog';
import { IconChevronDown, IconChevronRight } from '@shared/ui/icons';
import { mappingCounts } from './helpers';
import {
  Empty,
  Muted,
  SectionTitle,
  TabBar,
  TabButton,
  TitleMeta,
  TitleToggle,
  Wrap,
} from './styles';
import { RunDetailHeader } from './components/RunDetailHeader';
import { RunReviewBar } from './components/RunReviewBar';
import { PassportsSection } from './components/PassportsSection';
import { MappingsSection } from './components/MappingsSection';
import { SupplySection } from './components/SupplySection';
import { LongLeadSection } from './components/LongLeadSection';
import { SuppliersSection } from './components/SuppliersSection';
import { StockSection } from './components/StockSection';
import { AnomaliesSection } from './components/AnomaliesSection';

/**
 * Роут `/agents/mto/runs/:id` — карточка прогона модуля 1.1. Показывает живой
 * прогресс (пока активен), собранные паспорта качества (HITL + скачивание DOCX,
 * показатели качества раскрывашкой), аномалии (HITL) и решение по прогону
 * (утвердить → 409 при неразобранных критических аномалиях). Образец —
 * `pages/Economist/RunDetail`, но без сырых строк: показываем готовые паспорта.
 */
export function MtoRunDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can_write } = useAgentPermission('mto');

  const [poll, setPoll] = useState(0);
  // `currentData` — только данные этого прогона: при переходе к другому не
  // показываем на секунду прежний. Сбой очередного опроса не прячет карточку —
  // остаются последние полученные данные.
  const {
    currentData: data,
    isError,
    refetch,
  } = useGetMtoRunQuery(id ?? '', {
    skip: !id,
    pollingInterval: poll,
  });

  const [decide, { isLoading: deciding }] = useDecideMtoRunMutation();
  const [deleteRun, { isLoading: deleting }] = useDeleteMtoRunMutation();
  const [exportMemo, { isFetching: memoLoading }] = useLazyExportMtoMemoQuery();
  const [exportXlsx, { isFetching: xlsxLoading }] = useLazyExportMtoRunQuery();
  const [retryRun, { isLoading: retrying }] = useRetryMtoRunMutation();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Вид блока КП (2.3): плоский список строк предложений либо сравнение по
  // позициям. По умолчанию список — он отражает ровно то, что разобрано из
  // файлов этой обработки, без подмешивания цен из других прогонов.
  const [offersView, setOffersView] = useState<'list' | 'comparison'>('list');
  // Сопоставление с 1С: у ведомости по умолчанию свёрнуто (код 1С — в её
  // таблице), у остальных — раскрыто. `null` — выбор по модулю.
  const [mapOpen, setMapOpen] = useState<boolean | null>(null);

  const { setActiveProjectId } = useActiveProjectContext();
  const run = data?.run;
  const runProjectId = run ? runRaw(run).project_id : null;

  useEffect(() => {
    if (runProjectId) {
      setActiveProjectId(runProjectId);
    }
  }, [runProjectId, setActiveProjectId]);

  // Статус/прогресс прогона обновляются асинхронно — пока прогон активен,
  // опрашиваем карточку каждые 3с. На терминальном статусе poll выключаем.
  useEffect(() => {
    setPoll(run && isMtoRunActive(run.status) ? 3000 : 0);
  }, [run]);

  if (!data || !run) {
    return (
      <Card>
        {isError ? (
          <Muted role="alert">
            Не удалось загрузить обработку — возможно, она удалена.{' '}
            <Button type="button" $variant="ghost" $size="sm" onClick={() => refetch()}>
              Повторить
            </Button>
          </Muted>
        ) : (
          <Muted role="status">Загрузка обработки…</Muted>
        )}
      </Card>
    );
  }

  const passports = data.passports;
  const materials = data.materials;
  const mappings = data.mappings;
  const orders = data.orders;
  const deliveries = data.deliveries;
  const longlead = data.longlead;
  const offers = data.offers;
  const stock = data.stock;
  const anomalies = data.anomalies;

  // Прогон одного из модулей: 1.1 даёт паспорта; 1.2 — позиции потребности из РД;
  // 1.4 — позиции сверки (РД + заявки); 2.1 — заказы и приходы (контроль
  // снабжения); 2.2 — длинноцикловые позиции; 2.3 — предложения поставщиков (КП);
  // 2.4 — складские остатки. Модули 1.2 и 1.4 показывают таблицу позиций
  // (materials), различаясь подписями и ролью источника.
  const isReconcile = run.module === 'reconcile';
  const isVedomost = run.module === 'rd_extract';
  const isMaterialsModule = isVedomost || isReconcile;
  // Длинноцикловые позиции (2.2): определяем по модулю либо по наличию данных
  // (на случай прогонов без проставленного module). Проверяем ДО supply, чтобы
  // long_lead не перехватился им (у обоих read-only таблицы, но колонки разные).
  const isLongLead = run.module === 'long_lead' || longlead.length > 0;
  // Реестр поставщиков и КП (2.3) / остатки (2.4): определяем по модулю либо по
  // наличию данных. Гасим взаимные коллизии явными guard'ами (у каждого модуля
  // свой набор данных — пересечений в норме нет).
  const isSuppliers = !isLongLead && (run.module === 'suppliers' || offers.length > 0);
  const isStock = !isLongLead && !isSuppliers && (run.module === 'stock' || stock.length > 0);
  // Контроль снабжения (2.1): план-факт по поставкам. Определяем по модулю либо
  // по наличию данных (на случай прогонов без проставленного module).
  const isSupply =
    !isLongLead &&
    !isSuppliers &&
    !isStock &&
    (run.module === 'supply' || orders.length > 0 || deliveries.length > 0);

  const mappingsOpen = mapOpen ?? !isVedomost;
  const setMappingsOpen = (v: boolean) => setMapOpen(v);
  const mapCounts = mappingCounts(mappings);
  const title = run.summary?.trim() || `Обработка от ${fmtDateTime(run.created_at)}`;
  // Возврат к списку — в раздел того же модуля (как у Экономиста), иначе к
  // сводному реестру всех обработок.
  const backTo = isMtoModuleId(run.module)
    ? `/agents/mto/runs?module=${run.module}`
    : '/agents/mto/runs';

  // Гейт утверждения: только у паспортов неразобранные критические замечания
  // блокируют сборку (backend вернёт 409). В остальных модулях замечания —
  // сигналы, утверждение не блокируют.
  const blockingCritical = approvalBlockingCount(run.module, anomalies);

  async function handleDecision(decision: 'accepted' | 'rejected') {
    if (!id) return;
    setActionError(null);
    try {
      await decide({ id, decision }).unwrap();
    } catch (err) {
      setActionError(apiErrorMessage(err) ?? 'Не удалось сохранить решение. Попробуйте ещё раз.');
      // eslint-disable-next-line no-console
      console.error('decideMtoRun failed', err);
    }
  }

  async function handleDelete() {
    if (!id) return;
    const mod = run?.module;
    try {
      await deleteRun(id).unwrap();
      navigate(isMtoModuleId(mod) ? `/agents/mto/runs?module=${mod}` : '/agents/mto/runs');
    } catch (err) {
      setActionError('Не удалось удалить обработку.');
      // eslint-disable-next-line no-console
      console.error('deleteMtoRun failed', err);
    }
  }

  async function handleExcel() {
    if (!id || !run) return;
    setActionError(null);
    try {
      const blob = await exportXlsx(id).unwrap();
      saveBlob(blob, exportFileName(run));
    } catch (err) {
      setActionError(apiErrorMessage(err) ?? 'Не удалось сформировать выгрузку в Excel.');
    }
  }

  async function handleRetry() {
    if (!id) return;
    setActionError(null);
    try {
      await retryRun(id).unwrap();
    } catch (err) {
      setActionError(apiErrorMessage(err) ?? 'Не удалось повторить обработку.');
    }
  }

  // Проект служебной записки (2.5): генерация on-demand на backend (сбор
  // существенных аномалий → текст LLM → DOCX), скачиваем как Blob.
  async function handleMemoDownload() {
    if (!id) return;
    setActionError(null);
    try {
      const blob = await exportMemo(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `memo_${id}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(apiErrorMessage(err) ?? 'Не удалось сформировать служебную записку.');
      // eslint-disable-next-line no-console
      console.error('exportMtoMemo failed', err);
    }
  }

  const countsLabel = `${
    isLongLead
      ? `позиций: ${longlead.length}`
      : isSuppliers
        ? `предложений: ${offers.length}`
        : isStock
          ? `позиций: ${stock.length}`
          : isSupply
            ? `заказов: ${orders.length} · приходов: ${deliveries.length}`
            : isMaterialsModule
              ? `строк: ${materials.length}`
              : `паспортов: ${passports.length}`
  }${!isMtoRunActive(run.status) ? ` · замечаний: ${anomalies.length}` : ''}`;

  return (
    <Wrap>
      <RunDetailHeader
        run={run}
        title={title}
        backTo={backTo}
        countsLabel={countsLabel}
        isSuppliers={isSuppliers}
        isVedomost={isVedomost}
        canWrite={can_write}
        actionError={actionError}
        xlsxLoading={xlsxLoading}
        memoLoading={memoLoading}
        retrying={retrying}
        deleting={deleting}
        onExcel={handleExcel}
        onMemo={handleMemoDownload}
        onRetry={handleRetry}
        onDeleteClick={() => setConfirmOpen(true)}
        onNavigateReconcile={() => navigate(`/agents/mto/runs?module=reconcile&rd_run=${run.id}`)}
      />

      <RunReviewBar
        run={run}
        isLongLead={isLongLead}
        isSuppliers={isSuppliers}
        isStock={isStock}
        isSupply={isSupply}
        isVedomost={isVedomost}
        isReconcile={isReconcile}
        canWrite={can_write}
        deciding={deciding}
        blockingCritical={blockingCritical}
        onDecision={handleDecision}
      />

      {/* Блок 1 — предметный результат прогона: для 2.2 длинноцикловые позиции,
          для 2.1 заказы/приходы (план-факт), для 1.2 позиции потребности из РД,
          для 1.4 позиции сверки (РД + заявки), для 1.1 — паспорта качества. */}
      {isLongLead ? (
        <Card>
          <SectionTitle>
            Длинноцикловые позиции <Badge $tone="neutral">{longlead.length}</Badge>
          </SectionTitle>
          <LongLeadSection items={longlead} />
        </Card>
      ) : isSuppliers ? (
        <Card>
          <SectionTitle>
            Коммерческие предложения (КП) <Badge $tone="neutral">{offers.length}</Badge>
          </SectionTitle>
          <TabBar role="tablist" aria-label="Вид предложений">
            <TabButton
              type="button"
              role="tab"
              $active={offersView === 'list'}
              aria-selected={offersView === 'list'}
              onClick={() => setOffersView('list')}
            >
              Список
            </TabButton>
            <TabButton
              type="button"
              role="tab"
              $active={offersView === 'comparison'}
              aria-selected={offersView === 'comparison'}
              onClick={() => setOffersView('comparison')}
            >
              Сравнение
            </TabButton>
          </TabBar>
          {offersView === 'list' ? (
            <SuppliersSection offers={offers} />
          ) : (
            <ComparisonSection
              runId={run.id}
              canWrite={can_write}
              onError={(msg) => setActionError(msg || null)}
            />
          )}
        </Card>
      ) : isStock ? (
        <Card>
          <SectionTitle>
            Складские остатки <Badge $tone="neutral">{stock.length}</Badge>
          </SectionTitle>
          <StockSection items={stock} />
        </Card>
      ) : isSupply ? (
        <Card>
          <SectionTitle>Поставки (план-факт)</SectionTitle>
          <SupplySection
            orders={orders}
            deliveries={deliveries}
            canWrite={can_write}
            projectId={runProjectId}
            onError={(msg) => setActionError(msg || null)}
          />
        </Card>
      ) : isVedomost ? (
        run.status === 'completed' ? (
          <MtoVedomostView
            run={run}
            materials={materials}
            mappings={mappings}
            canWrite={can_write}
          />
        ) : null
      ) : isReconcile ? (
        run.status === 'completed' ? (
          <MtoReconcileView run={run} materials={materials} />
        ) : null
      ) : (
        <Card>
          <SectionTitle>
            Паспорта качества <Badge $tone="neutral">{passports.length}</Badge>
          </SectionTitle>
          <PassportsSection
            passports={passports}
            runId={run.id}
            canWrite={can_write}
            onError={(msg) => setActionError(msg || null)}
          />
        </Card>
      )}

      {/* Блок 1.3 — Сопоставление с НСИ. Показываем, только если у прогона есть
          сопоставления (для прогонов без нормализации — mappings=[]). */}
      {mappings.length > 0 && (
        <Card>
          <SectionTitle>
            <TitleToggle
              type="button"
              aria-expanded={mappingsOpen}
              onClick={() => setMappingsOpen(!mappingsOpen)}
            >
              {mappingsOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
              Сопоставление со справочником 1С <Badge $tone="neutral">{mappings.length}</Badge>
            </TitleToggle>
            <TitleMeta>
              привязано: {mapCounts.linked} · на проверку: {mapCounts.pending}
              {mapCounts.none > 0 ? ` · не найдено в справочнике: ${mapCounts.none}` : ''}
            </TitleMeta>
          </SectionTitle>
          {mappingsOpen && (
            <MappingsSection
              mappings={mappings}
              runId={run.id}
              canWrite={can_write}
              onError={(msg) => setActionError(msg || null)}
            />
          )}
        </Card>
      )}

      {/* Блок 2 — Замечания (аномалии). Пока обработка идёт, их список
          неполный — не показываем «замечаний нет». */}
      <Card>
        <SectionTitle>
          Замечания{' '}
          {!isMtoRunActive(run.status) && <Badge $tone="neutral">{anomalies.length}</Badge>}
        </SectionTitle>
        {isMtoRunActive(run.status) ? (
          <Empty>Появятся после завершения обработки.</Empty>
        ) : (
          <AnomaliesSection anomalies={anomalies} runId={run.id} canWrite={can_write} />
        )}
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        title="Удалить обработку?"
        warning="Обработка и все её результаты (паспорта, позиции, сопоставления, расхождения) будут удалены безвозвратно. Это действие нельзя отменить."
        confirmText="удалить"
        confirmLabel="Введите слово «удалить» для подтверждения"
        confirmButtonLabel="Удалить обработку"
        busyLabel="Удаление…"
        isBusy={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Wrap>
  );
}
